import { Box, Grid, Typography } from '@mui/material';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import scannerIcon from '../../assets/images/Bar_code_scan.png';
import darkScannerIcon from '../../assets/images/darkbarcode.png';
import { StyledTable, ScanOrEnterBarcodeForm } from '../../components';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import {
	DARK_MODE,
	DEFAULT_COLUMNS,
	ERROR_MESSAGE,
	COLUMNS_TO_HIDE_FOR_CHASSIS_LINE,
	LOADING_MESSAGE,
	CHASIS_LINE_IDS,
	OPERATIONS,
	ADDITIONAL_COLUMNS_FOR_CHASSIS_LINE,
	REDIRECT_URLS,
} from '../../constants';
import {
	useRedirectToNextScreen,
	useResolutionScale,
	useAutoFocus,
} from '../../hooks';
import { useNavigate } from 'react-router-dom';
import {
	fetchLineUpcomingParts,
	updateCurrentLineItem,
	vinAvailability,
	receiveAsnOrAgsn,
	fetchProcessedData,
} from '../../redux/actionCreators/station';
import { markComplete } from '../../redux/actionCreators/printBarcode';
import { updateOperations } from '../../redux/actionCreators/operations';
import {
	getNavigableOperations,
	getCurrentPartStatus,
	getNextOperation,
} from '../../utils/operationFlow';
import styles from './ScanVin.module.scss';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import TableActionBar from '../../components/TableActionBar/TableActionBar';

const mapDispatchToProps = (dispatch) => ({
	fetchLineUpcomingParts: (successCallback, errorCallback) =>
		dispatch(fetchLineUpcomingParts(successCallback, errorCallback)),
	vinAvailability: (payload, successCallback, errorCallback) =>
		dispatch(vinAvailability(payload, successCallback, errorCallback)),
	receiveAsnOrAgsn: (sequenceNumber, payload, successCallback, errorCallback) =>
		dispatch(
			receiveAsnOrAgsn(sequenceNumber, payload, successCallback, errorCallback),
		),
	updateCurrentLineItem: (currentLineItem) =>
		dispatch(updateCurrentLineItem(currentLineItem)),
	updateOperations: (data) => dispatch(updateOperations(data)),
	markComplete: (successCallback, errorCallback, sequenceNumber) =>
		dispatch(markComplete(successCallback, errorCallback, sequenceNumber)),
	fetchProcessedData: (stationName, successCallback, errorCallback) =>
		dispatch(fetchProcessedData(stationName, successCallback, errorCallback)),
});

const mapStateToProps = (state) => {
	return {
		user: state.user,
		themeMode: state.theme.themeMode,
		currentOperation: state.operations.currentOperation,
		allowedOperations: state.operations.allowedOperations,
		workStreamType: state.user.workStreamType,
	};
};

/** Columns for the Last Processed table — mapped to /station-history API response */
const lastProcessedColumns = [
	{
		width: 300,
		title: 'LAST PROCESSED VIN',
		field: 'lastProcessedVin',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => row?.lastProcessedVin ?? '—',
	},
	{
		width: 200,
		title: 'KSPEC',
		field: 'kspec',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => row?.kspec ?? '—',
	},
	{
		width: 360,
		title: 'KSPEC DESCRIPTION',
		field: 'kspecDescription',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => row?.kspecDescription ?? '—',
	},
	{
		width: 240,
		title: 'LAST PROCESSED AT',
		field: 'lastProcessedAt',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => {
			if (!row?.lastProcessedAt) return '—';
			try {
				return new Date(row.lastProcessedAt).toLocaleString('en-IN', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false,
				});
			} catch {
				return row.lastProcessedAt;
			}
		},
	},
	{
		width: 160,
		title: 'TOTAL COUNT',
		field: 'totalCount',
		align: 'center',
		enableSearch: false,
		enableFilter: false,
		render: (row) => row?.totalCount ?? '—',
	},
];
export const UpdateScanVIN = ({
	selectedRows,
	selectedRowObjects,
	themeMode,
	handleDrop,
	is4K,
}) => {
	const [vinValue, setVinValue] = useState('');
	const [resetInput, setResetInput] = useState(0);

	// Use auto-focus hook with 10 second timeout and global context
	const { inputRef } = useAutoFocus({
		timeout: 10000, // 10 seconds
		enabled: true,
		componentId: 'scan-vin-input', // Unique ID for global context
		onFocus: () => { },
	});

	const onSubmit = (scannedValue, resetBarcodeValue) => {
		// Validate that VIN is not empty or null
		if (!scannedValue || scannedValue.trim() === '') {
			console.error('VIN cannot be empty');
			return;
		}

		const updatedScannedValue = scannedValue.substring(0, 17);
		handleDrop(selectedRows, updatedScannedValue, resetBarcodeValue);
	};

	// Check if scan button should be disabled
	// Button should be disabled if no checkbox is selected OR VIN input is empty
	const isScanDisabled =
		// !selectedRowObjects ||
		// selectedRowObjects.length === 0 ||
		!vinValue ||
		vinValue.trim() === '';

	// Initial focus when component mounts
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	// Clear VIN value when no checkbox is selected
	useEffect(() => {
		if (!selectedRowObjects || selectedRowObjects.length === 0) {
			setVinValue('');
			// Trigger input reset by changing the key
			setResetInput((prev) => prev + 1);
		}
	}, [selectedRowObjects]);

	return (
		<Grid container className={styles.vinInputRow}>
			<Grid item className={styles.vinHeaderSection}>
				{/* <Typography 
					variant="h4" 
					className={`${styles.vinHeaderText} ${themeMode === DARK_MODE ? styles.vinHeaderTextDark : styles.vinHeaderTextLight}`}
				>
					VIN
				</Typography> */}
				<Box className={styles.scannerIconBox}>
					<img
						src={themeMode === DARK_MODE ? darkScannerIcon : scannerIcon}
						alt="scan"
						className={styles.scannerIcon}
					/>
				</Box>
			</Grid>
			<Grid item className={styles.vinInputSection}>
				<ScanOrEnterBarcodeForm
					resetTrigger={resetInput}
					enableAutoEnter
					inputRef={inputRef}
					isAlphaNumericValidation={false}
					onSubmit={onSubmit}
					disabled={isScanDisabled}
					onValueChange={setVinValue}
					textInputSx={{
						background: '#F9FBFD',
						border: '1px solid #231F20',
						borderTopRightRadius: '22px',
						borderBottomRightRadius: '22px',
						paddingLeft: '12px',
						'& .MuiInputBase-input': {
							color: '#202020',
						},
						'& .MuiInputBase-input::placeholder': {
							color: '#202020',
							opacity: 1,
						},
						'&:hover': {
							background: '#F9FBFD',
							borderColor: '#231F20',
						},
						'&.Mui-focused': {
							background: '#F9FBFD',
							borderColor: '#231F20',
							boxShadow: 'none',
						},
					}}
					forwardBtnSx={{
						height: 56,
						background: isScanDisabled
							? '#171c5fff'
							: 'linear-gradient(180deg, #0F107B 0%, #1B1EE1 100%)',
						border: '1px solid #17201C',
						borderRadius: '20px',
						'&:hover': {
							background: isScanDisabled ? '#CCCCCC' : '#171c5fff',
						},
					}}
				/>
			</Grid>
		</Grid>
	);
};

export const ScanVin = ({
	themeMode,
	fetchLineUpcomingParts,
	vinAvailability,
	receiveAsnOrAgsn,
	updateCurrentLineItem,
	updateOperations,
	markComplete,
	user,
	allowedOperations = [],
	fetchProcessedData = () => {},
}) => {
	const redirect = useRedirectToNextScreen();
	const navigate = useNavigate();
	const { showMessageOverlay } = useMessageContextManager();
	const { is4K, isAssemblyLine } = useResolutionScale();

	const [upcomingLineItems, setUpcomingLineItems] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const [selectedRowObjects, setSelectedRowObjects] = useState([]);

	// Calculate responsive table heights using resolution scaling
	const rowHeight = isAssemblyLine ? 120 : 60;
	const atStationTableHeight = 8 * rowHeight; // 8 rows

	const hasCheckedRedirection = useRef(false);

	// ── Verify operation flow / redirection on load ───────────────────────
	useEffect(() => {
		if (hasCheckedRedirection.current) return;
		if (!allowedOperations || allowedOperations.length === 0) return;

		hasCheckedRedirection.current = true;

		const checkRedirection = async () => {

			let closeOverlay;
			try {
				closeOverlay = showMessageOverlay({
					contextText:
						LOADING_MESSAGE.CHECKING_STATION_STATUS ||
						'Checking station status...',
					loading: true,
				});

				const navigableOps = getNavigableOperations(allowedOperations);
				const firstOperation = navigableOps.includes(OPERATIONS.RECEIVE_SN)
					? OPERATIONS.RECEIVE_SN
					: navigableOps[0];

				if (firstOperation === OPERATIONS.RECEIVE_SN) {
					try {
						const currentPart = await getCurrentPartStatus();
						if (currentPart?.serialNumber) {
							// For ENGRAVE stations: if a part exists, always go to engraving
							// regardless of whether all operations are already completed.
							if (allowedOperations.includes(OPERATIONS.ENGRAVING)) {
								updateCurrentLineItem?.(currentPart);
								updateOperations?.({ currentOperation: OPERATIONS.ENGRAVING });
								closeOverlay?.();
								navigate?.(REDIRECT_URLS[OPERATIONS.ENGRAVING]);
								return;
							}

							if (currentPart?.completedOperations?.includes(OPERATIONS.RECEIVE_SN)) {
								const nextOp = getNextOperation(
									currentPart.completedOperations,
									allowedOperations,
								);
								const nextPath = REDIRECT_URLS[nextOp];
								if (nextPath && nextPath !== REDIRECT_URLS[OPERATIONS.RECEIVE_SN]) {
									updateCurrentLineItem?.(currentPart);
									updateOperations?.({ currentOperation: nextOp });
									closeOverlay?.();
									navigate?.(nextPath);
									return;
								}
							}
						}
					} catch (e) {
						console.error('Error checking operation redirection in ScanVin:', e);
					}
					closeOverlay?.();
					return;
				}

				// If RECEIVE_SN is not in allowed operations, redirect to first navigable operation screen
				if (firstOperation && firstOperation !== OPERATIONS.RECEIVE_SN) {
					const redirectUrl = REDIRECT_URLS[firstOperation];
					if (redirectUrl && redirectUrl !== '/scan-vin') {
						updateOperations?.({ currentOperation: firstOperation });
						closeOverlay?.();
						navigate?.(redirectUrl);
						return;
					}
				}
				closeOverlay?.();
			} catch (err) {
				console.error('Error verifying operation redirection in ScanVin:', err);
				closeOverlay?.();
			}
		};

		checkRedirection();
	}, [allowedOperations]);

	// ── Last Processed: fetch from /station-history API ────────────────────
	const [lastProcessedData, setLastProcessedData] = useState([]);

	// Keeping station name as null for all stations except WCPA
	const loadStationHistory = useCallback(() => {
		fetchProcessedData(
			null,
			(response) => {
				const dataArray = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : response ? [response] : [];
				const mapped = dataArray
					.filter((item) => item.vin || item.lastProcessedVin || item.serialNumber)
					.map((item, index) => ({
						...item,
						id: item.dtStationId ?? index,
						lastProcessedVin: item.vin ?? item.lastProcessedVin ?? item.serialNumber,
						lastProcessedAt: item.processedAt ?? item.lastProcessedAt,
					}));
				setLastProcessedData(mapped);
			},
			() => {
				setLastProcessedData([]);
			},
		);
	}, [fetchProcessedData]);

	// Fetch on mount
	useEffect(() => {
		loadStationHistory();
	}, [loadStationHistory]);

	// Dynamic height calculation for Last Processed table
	const lastProcessedTableHeight = lastProcessedData.length > 0
		? rowHeight * lastProcessedData.length
		: rowHeight;

	const ChasisLineColums = [
		...DEFAULT_COLUMNS,
		...ADDITIONAL_COLUMNS_FOR_CHASSIS_LINE,
	];

	const filteredDefaultColumns = useMemo(() => {
		return DEFAULT_COLUMNS.map((col) => {
			// Respect explicit non-sortable columns; otherwise default to sortable
			if (col.sortable === false) return col;
			return { ...col, sortable: true };
		});
	}, [DEFAULT_COLUMNS]);

	const chasisFilteredDefaultColumns = useMemo(() => {
		return ChasisLineColums.map((col) => {
			if (col.sortable === false) return col;
			return { ...col, sortable: true };
		});
	}, [ChasisLineColums]);

	const getUpcomingData = async () =>
		fetchLineUpcomingParts(
			(data) => {
				if (data && Array.isArray(data)) {
					setUpcomingLineItems(
						data.map((element) => {
							return {
								...element,
								id: element.sequenceNumber,
								readyForDropSequenceNumber:
									element.metaData?.readyForDropSequenceNumber,
								vcDescription: element.metaData?.vcDescription,
								vcPartNumber: element.metaData?.vcPartNumber,
								vcSequenceNumber: element.metaData?.vcSequenceNumber,
								vcNumber: element.metaData?.vcNumber,
								shiftLabel: element.metaData?.shiftLabel,
								majorModel: element.metaData?.majormodel,
								country: element.metaData?.country,
								isVinScannedOnce: false,
							};
						}),
					);
				}
			},
			(error) => {
				console.error('Error fetching upcoming parts:', error);
			},
		);

	const filteredOriginalDefaultColumns = useMemo(() => {
		if (CHASIS_LINE_IDS.includes(user?.lineId)) {
			return chasisFilteredDefaultColumns.filter((res) => {
				return !COLUMNS_TO_HIDE_FOR_CHASSIS_LINE.includes(res.field);
			});
		}
		return filteredDefaultColumns; // Return the array directly
	}, [
		filteredDefaultColumns,
		chasisFilteredDefaultColumns,
		user?.lineId,
		CHASIS_LINE_IDS,
	]);



	const handleDrop = (selectedRows, scannedValue, resetBarcodeValue) => {
		// Require exactly one selected row for VIN scanning
		// if (!selectedRows || selectedRows.length !== 1) {
		// 	console.error('Exactly one row must be selected for VIN scanning');
		// 	showMessageOverlay({
		// 		contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}VIN Scan`,
		// 		info: 'Please select exactly one item from the table to scan.',
		// 		error: true,
		// 	});
		// 	return;
		// }

		// Use selectedRowObjects instead of selectedRows for the actual row data
		// const activeRow =
		// 	selectedRowObjects.length > 0 ? selectedRowObjects[0] : null;
		// if (!activeRow) {
		// 	console.error('No valid row selected for VIN scanning');
		// 	showMessageOverlay({
		// 		contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}VIN Scan`,
		// 		info: 'Please select a valid item from the table to scan.',
		// 		error: true,
		// 	});
		// 	return;
		// }


		const payload = {
			serialNumber: scannedValue,
			// vcPartNumber: activeRow.vcPartNumber || activeRow.metaData?.vcPartNumber,
			// sequenceNumber: activeRow.sequenceNumber,
		};
		// Show loading popup while calling VIN validation API
		const closeMesssageOverlay = showMessageOverlay({
			contextText: LOADING_MESSAGE.CHECKING_VIN_AVAILABLITY,
			loading: true,
		});

		// JLR Line Flow: Always call VIN validation API
		vinAvailability(
			payload,
			(vinAvailabilityRes) => {
				const activeRow = upcomingLineItems.find(item => item.sequenceNumber === vinAvailabilityRes.asn);

				// Build a fallback row from vinAvailabilityRes when activeRow isn't found
				// in the upcomingLineItems list. This ensures currentLineItem always carries
				// the display fields (displaySequenceNumber, vcPartNumber, etc.) that the
				// "Last Processed" table columns need.
				const fallbackRow = {
					sequenceNumber: vinAvailabilityRes.asn,
					displaySequenceNumber: vinAvailabilityRes.displaySequenceNumber || vinAvailabilityRes.asn,
					vcPartNumber: vinAvailabilityRes.vcPartNumber || vinAvailabilityRes.metaData?.vcPartNumber || '',
					description: vinAvailabilityRes.description || vinAvailabilityRes.metaData?.vcDescription || '',
					model: vinAvailabilityRes.model || vinAvailabilityRes.metaData?.majormodel || '',
				};

				const resolvedRow = activeRow || fallbackRow;
				const resolvedSequenceNumber = resolvedRow.sequenceNumber || vinAvailabilityRes.asn;

				// VIN validation successful (200), now call /line/receive/ API
				const receivePayload = {
					serialNumber: scannedValue,
				};
				receiveAsnOrAgsn(
					vinAvailabilityRes.asn,
					receivePayload,
					() => {
						// /line/receive/ API successful
						closeMesssageOverlay();
						resetBarcodeValue();

						// Update current line item with RECEIVE_SN completed
						const updatedLineItem = {
							...resolvedRow,
							serialNumber: scannedValue,
							isVinScannedOnce: false,
							completedOperations: [OPERATIONS.RECEIVE_SN],
						};
						updateCurrentLineItem(updatedLineItem);

						// Determine next operation based on allowed operations
						const nextOperation = getNextOperation(
							[OPERATIONS.RECEIVE_SN],
							allowedOperations,
						);
						updateOperations({ currentOperation: nextOperation });

						// If user only has RECEIVE_SN operation, call markComplete to complete the part
						if (
							allowedOperations.length === 1 &&
							allowedOperations[0] === OPERATIONS.RECEIVE_SN
						) {
							markComplete(
								() => {
									// Mark complete successful — refresh Last Processed table then navigate
									loadStationHistory();
									navigate('/scan-vin');
								},
								(error) => {
									showMessageOverlay({
										contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}Mark Complete Failed`,
										info:
											error?.response?.data?.message ||
											'Failed to mark operation complete.',
										error: true,
									});
								},
								resolvedSequenceNumber,
							);
						} else {
							// Navigate to next operation screen
							const nextPath = REDIRECT_URLS[nextOperation] || '/scan-vin';
							navigate(nextPath);
						}
					},
					(error) => {
						// /line/receive/ API failed
						closeMesssageOverlay();
						showMessageOverlay({
							contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}Receive Operation Failed`,
							info:
								error?.response?.data?.message || 'Failed to receive the part',
							error: true,
						});
						resetBarcodeValue();
						if (resolvedSequenceNumber) {
							setUpcomingLineItems((prevState) =>
								prevState.map((asn) => {
									if (asn.sequenceNumber === resolvedSequenceNumber)
										return { ...asn, isVinScannedOnce: true };
									return { ...asn };
								}),
							);
						}
					},
				);
			},
			(error) => {
				// VIN validation failed
				closeMesssageOverlay();
				showMessageOverlay({
					contextText: `${ERROR_MESSAGE.ERROR_CONTEXT_TEXT}VIN Validation Failed`,
					info: error?.response?.data?.message,
					error: true,
				});
				resetBarcodeValue();
			},
		);
	};

	const handleSelectionChange = (selectedIds, selectedRow) => {
		setSelectedRows(selectedIds);
		// Store the actual row objects for API calls
		if (selectedRow) {
			setSelectedRowObjects([selectedRow]);
		} else {
			setSelectedRowObjects([]);
		}
	};

	useAjaxCallbackWithIntervals(false, false, getUpcomingData);
	const [filter, setFilter] = useState({});
	const [columns, setColumns] = useState(filteredOriginalDefaultColumns);

	const filteredRowsCount = useMemo(() => {
		return upcomingLineItems.filter((row) => {
			return Object.entries(filter).every(([key, value]) => {
				if (!value?.length) return true;
				return value.includes(row[key]);
			});
		}).length;
	}, [upcomingLineItems, filter]);

	const { scaleValue } = useResolutionScale();

	return (
		<Grid container sx={{ paddingTop: 0, marginTop: 0 }}>
			<Grid item xs={12}>
				<Grid
					container
					direction="row"
					justifyContent="space-between"
					sx={{ paddingTop: 0, marginTop: 0, gap: 0 }}
				>
					<Grid item xs={12} sx={{ paddingTop: 0, marginTop: 0 }}>
						<Grid
							container
							alignItems="center"
							flex={1}
							justifyContent="space-between"
						>
							<Grid item>
								<Typography
									variant="inherit"
									sx={{
										fontWeight: 600,
										fontSize: `${scaleValue(24)}px`,
									}}
								>
									At Station ({filteredRowsCount})
								</Typography>
							</Grid>

							<Grid item>
								<TableActionBar
									showColumns={true}
									showFilter={true}
									rows={upcomingLineItems}
									columns={columns}
									defaultColumns={[...filteredOriginalDefaultColumns]}
									onColumnChange={setColumns}
									onFilterChange={setFilter}
								/>
							</Grid>
						</Grid>

						<StyledTable
							rows={upcomingLineItems}
							columns={columns}
							filters={filter}
							showCheckBox={false}
							elevated={true}
							selectedRows={selectedRows}
							isRowClickCopy={true}
						/>
					</Grid>

					{/* VIN Input Section - Outside of BasicTable */}
					<Grid item xs={12} marginTop={2}>
						<UpdateScanVIN
							selectedRows={selectedRows}
							selectedRowObjects={selectedRowObjects}
							themeMode={themeMode}
							handleDrop={handleDrop}
							is4K={is4K}
						/>
					</Grid>

					<Grid item xs={12} marginTop={3}>
						<StyledTable
							title={`Last Processed`}
							rows={lastProcessedData}
							columns={lastProcessedColumns}
							showCheckBox={false}
							elevated={true}
							forceHeight={lastProcessedTableHeight}
							showFilter={false}
							showColumns={false}
							isRowClickCopy={true}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ScanVin);
